import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { IdentityStorage } from 'storage-service-lib';
import { ServiceProviderServiceFactory } from '../../../../services/service-providers/service-provider-service.factory';
import { DOCUSIGN_TRIGGER_IDS, ITriggerConfig, TriggerIds } from '../actionConfigs/shared-action-configurations';
import { BullhornCreateNoteComponent } from '../bullhorn-create-note/bullhorn-create-note.component';
import { SalesforceCreateCallComponent } from '../salesforce-create-call/salesforce-create-call.component';
import { UpdateFieldComponent } from '../update-field/update-field.component';
import { ActionService } from './action.service';

@Component({
    selector: 'app-trigger-accordion',
    templateUrl: './trigger-accordion.component.html',
    styleUrls: ['./trigger-accordion.component.scss'],
    standalone: false
})
export class TriggerAccordionComponent implements OnInit, OnChanges {
  protected readonly BullhornCreateNoteComponent = BullhornCreateNoteComponent;
  protected readonly UpdateFieldComponent = UpdateFieldComponent;
  protected readonly SalesforceCreateCallComponent = SalesforceCreateCallComponent;

  get actions(): FormArray<FormGroup> {
    return this.actionFormGroup?.get('properties')?.get('actions') as FormArray<FormGroup>;
  }

  get triggerId(): string {
    return this.actionFormGroup?.value?.id ?? '';
  }

  protected readonly ClientCorporation = BullhornEntityTypes.ClientCorporation;

  @Input() actionFormGroup: FormGroup;
  @Input() entity: string;
  @Input() isDocusignEnabled: boolean;

  title = '';
  description = '';
  triggerSettings: ITriggerConfig;

  get shouldShowAccordion(): boolean {
    if (!this.triggerSettings) {
      return false;
    }

    if (this.triggerId === TriggerIds.SAVE_TO_ENTITY) {
      return true;
    }

    if (DOCUSIGN_TRIGGER_IDS.includes(this.triggerId as typeof DOCUSIGN_TRIGGER_IDS[number])) {
      return this.isDocusignEnabled === true;
    }

    return false;
  }

  constructor(private identityStorage: IdentityStorage, private serviceProviderServiceFactory: ServiceProviderServiceFactory) {}

  static buildActionFormGroup(action: any, namespace: string) {
    const triggerId = action?.id;
    return new FormGroup({
      id: new FormControl(action?.id),
      type: new FormControl(action?.type),
      properties: new FormGroup({
        actions: this.buildActions(action?.properties?.actions, namespace, triggerId),
      }),
    });
  }

  private static buildActions(actions: any[], namespace: string, triggerId?: string) {
    if (!actions) {
      return undefined;
    }
    const actionArray = new FormArray([]);
    actions.forEach((action) => {
      actionArray.push(ActionService.getActionFormGroupByAction(action, namespace, triggerId));
    });
    return actionArray;
  }

  ngOnInit() {
    this.applySettings();
  }

  ngOnChanges(changes: SimpleChanges) {
    const entityChange = changes['entity'];
    if (entityChange && !entityChange.firstChange) {
      this.handleEntityChange(entityChange.currentValue, entityChange.previousValue);
    }
  }

  getActionName(action: any): string {
    // eg: bullhorn.update-field
    return action?.value?.id?.split('.')?.pop();
  }

  getActionConfig(actionName: string): any {
    return this.triggerSettings?.actions[actionName];
  }

  updateFieldAllowed(actionName: string): boolean {
    return !!this.getActionConfig(actionName);
  }

  createNoteAllowed(actionName: string): boolean {
    return !!this.getActionConfig(actionName) && this.entity !== BullhornEntityTypes.ClientCorporation;
  }

  createCallAllowed(actionName: string): boolean {
    return !!this.getActionConfig(actionName);
  }

  addAction(actionName: string) {
    const namespace = this.identityStorage.get().namespace;
    this.actions.push(ActionService.getActionFormGroupByName(actionName, namespace, this.triggerId));
  }

  deleteAction(index: number) {
    this.actions.removeAt(index);
    this.actionFormGroup.markAsDirty();
  }

  private handleEntityChange(newEntity: string, oldEntity?: string) {
    if (newEntity === BullhornEntityTypes.ClientCorporation) {
      this.removeCreateNoteActions();
    }
  }

  private removeCreateNoteActions() {
    const actionsToRemove: number[] = [];

    this.actions.controls.forEach((control, index) => {
      if (control.value.id.includes(BullhornCreateNoteComponent.NAME)) {
        actionsToRemove.push(index);
      }
    });

    // Remove in reverse order to maintain correct indices
    actionsToRemove.reverse().forEach(index => {
      this.actions.removeAt(index);
    });

    if (actionsToRemove.length > 0) {
      this.actionFormGroup.markAsDirty();
    }
  }

  private applySettings() {
    const currentTriggerId = this.actionFormGroup.value.id;
    this.triggerSettings = this.findTriggerSettings(currentTriggerId);

    if (this.triggerSettings) {
      this.title = this.triggerSettings.title('docusign');
      this.description = this.triggerSettings.description('docusign');
    } else {
      this.title = '';
      this.description = '';
    }
  }

  private findTriggerSettings(id: string) {
    return this.serviceProviderServiceFactory
      ?.getServiceProviderService(this.identityStorage.get().namespace)
      ?.getTriggerConfigService()
      ?.getConfigs()
      ?.find((triggerConfig) => triggerConfig.id === id);
  }
}
